import type {
  ListResponseMetadata,
  ResourceAccessRequest,
  ResourceAccessRequestRecipient,
  ResourceAccessRequestsPost,
  ResourceAccessStateAnswer,
} from "@eai-toolkit/client";
import { ResourceAccessRequestsApi } from "@eai-toolkit/client";
import { filtersToQ } from "@store/utils/queryUtils";
import { constructOrderParameter } from "@store/utils/sortUtils";

import type {
  LocalAccessRequest,
  TargetType,
} from "../../modules/app/access-requests/data/types";
import { toLocalAccessRequest } from "../../modules/app/access-requests/data/types";
import { hydrateAccessRequestsQueryStateFromString } from "../queryState";
import { baseApi, handleQueryError } from "./baseApi";
import { axiosInstance } from "./utils";

const resourceAccessRequestsApi = new ResourceAccessRequestsApi(
  undefined,
  undefined,
  axiosInstance,
);

type AccessRequestsResponse = {
  items: LocalAccessRequest[];
  metadata?: ListResponseMetadata;
};

type CreateAccessRequestParams = {
  targetType: TargetType;
  targetId: string;
  body: ResourceAccessRequestsPost;
};

export const accessRequestsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccessRequests: builder.query<
      AccessRequestsResponse,
      // queryKey is a serialized string rather than the queryState object directly because
      // queryState contains a Map, which RTK Query cannot serialize for cache key comparison.
      { queryKey: string }
    >({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState =
            hydrateAccessRequestsQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const order = constructOrderParameter(sort.fieldName, sort.direction);

          const qParam = filtersToQ(filters);

          const response =
            await resourceAccessRequestsApi.v1ResourceAccessRequestsList(
              {
                limit,
                offset,
                order,
                human: true,
              },
              {
                params: {
                  ...(qParam ? { q: qParam } : {}),
                },
              },
            );

          const items = (response.data.items ?? []).map(
            (item: ResourceAccessRequestRecipient) =>
              toLocalAccessRequest(item),
          );

          return {
            data: {
              items,
              metadata: response.data.metadata,
            },
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.items as LocalAccessRequest[]).map(({ id }) => ({
                type: "AccessRequest" as const,
                id,
              })),
              { type: "AccessRequest", id: "LIST" },
            ]
          : [{ type: "AccessRequest", id: "LIST" }],
    }),

    getAccessRequestById: builder.query<
      ResourceAccessRequestRecipient,
      { requestId: string }
    >({
      queryFn: async ({ requestId }) => {
        try {
          const response =
            await resourceAccessRequestsApi.v1ResourceAccessRequestsGet({
              resourceAccessRequestsId: requestId,
              human: true,
            });
          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { requestId }) => [
        { type: "AccessRequest", id: requestId },
      ],
    }),

    createAccessRequest: builder.mutation<
      ResourceAccessRequest,
      CreateAccessRequestParams
    >({
      queryFn: async ({ targetType, targetId, body }) => {
        try {
          let response;
          switch (targetType) {
            case "role":
              response = await resourceAccessRequestsApi.v1RoleRequestAccess({
                roleId: targetId,
                resourceAccessRequestsPost: body,
                human: true,
              });
              break;
            case "user":
              response = await resourceAccessRequestsApi.v1UserRequestAccess({
                userId: targetId,
                resourceAccessRequestsPost: body,
                human: true,
              });
              break;
            case "team":
              response = await resourceAccessRequestsApi.v1TeamRequestAccess({
                teamId: targetId,
                resourceAccessRequestsPost: body,
                human: true,
              });
              break;
          }
          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      invalidatesTags: [{ type: "AccessRequest", id: "LIST" }],
    }),

    cancelAccessRequest: builder.mutation<
      ResourceAccessRequestRecipient,
      { requestId: string }
    >({
      queryFn: async ({ requestId }) => {
        try {
          const response =
            await resourceAccessRequestsApi.v1ResourceAccessRequestsCancel({
              resourceAccessRequestsId: requestId,
              human: true,
            });
          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      invalidatesTags: [{ type: "AccessRequest", id: "LIST" }],
      async onQueryStarted({ requestId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            accessRequestsApi.util.updateQueryData(
              "getAccessRequestById",
              { requestId },
              () => data,
            ),
          );
        } catch {
          // If the mutation fails, don't update the cache
        }
      },
    }),

    processAccessRequest: builder.mutation<
      ResourceAccessRequestRecipient,
      {
        requestId: string;
        state: ResourceAccessStateAnswer;
        processingRemarks?: string;
      }
    >({
      queryFn: async ({ requestId, state, processingRemarks }) => {
        try {
          const response =
            await resourceAccessRequestsApi.v1ResourceAccessRequestsProcessPost(
              {
                resourceAccessRequestsId: requestId,
                human: true,
                resourceAccessRequestProcess: {
                  state,
                  processingRemarks,
                },
              },
            );
          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      invalidatesTags: [{ type: "AccessRequest", id: "LIST" }],
      async onQueryStarted({ requestId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            accessRequestsApi.util.updateQueryData(
              "getAccessRequestById",
              { requestId },
              () => data,
            ),
          );
        } catch {
          // If the mutation fails, don't update the cache
        }
      },
    }),
  }),
});

export const {
  useGetAccessRequestsQuery,
  useGetAccessRequestByIdQuery,
  useCreateAccessRequestMutation,
  useCancelAccessRequestMutation,
  useProcessAccessRequestMutation,
} = accessRequestsApi;
